/**
 * Data Capture API Contract
 *
 * Endpoints:
 *   GET  /jobs/data-capture/:jobId                       → form responses, images, notes
 *   POST /jobs/data-capture/:jobId/notes                 → add a quality-check note
 *   POST /jobs/data-capture/:jobId/images/upload-url     → presigned S3 POST for image upload
 *   POST /jobs/data-capture/:jobId/images/complete       → persist image metadata after S3 upload
 *   POST /jobs/data-capture/:jobId/validate              → mark job as validated
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorResponseSchema = z.object({
  error: z.object({
    message: z.string(),
    code: z.string().optional(),
  }),
});

export const formResponseSchema = z.object({
  id: z.number(),
  job_task_id: z.number().nullable(),
  form_id: z.number().nullable(),
  form_name: z.string().nullable(),
  form_data: z.unknown(),
  status: z.number().nullable(),
  created_time: z.string().nullable(),
});

export const capturedImageSchema = z.object({
  id: z.number(),
  s3_object_key: z.string().nullable(),
  type: z.string().nullable(),
  created_time: z.string().nullable(),
});

export const noteSchema = z.object({
  id: z.number(),
  note_context: z.string().nullable(),
  created_at: z.string().nullable(),
  created_by_name: z.string().nullable(),
});

export const dataCaptureResponseSchema = z.object({
  form_responses: z.array(formResponseSchema),
  captured_images: z.array(capturedImageSchema),
  notes: z.array(noteSchema),
});

/**
 * Body for adding a quality-check note to a job.
 */
export const addNoteBodySchema = z.object({
  note: z.string().min(1).max(2000),
});

/**
 * Body for requesting a presigned S3 upload URL for a data-capture image.
 */
export const dataCaptureImageUploadUrlBodySchema = z.object({
  filename: z.string().min(1).max(255),
  content_type: z.string().min(1).max(200),
});

export const dataCaptureImageUploadUrlResponseSchema = z.object({
  url: z.string(),
  fields: z.record(z.string()),
  object_key: z.string(),
});

/**
 * Body for finalising a data-capture image upload after S3 upload completes.
 */
export const dataCaptureImageCompleteBodySchema = z.object({
  object_key: z.string().min(1).max(1024),
  content_type: z.string().min(1).max(200),
});

/**
 * Response shape for the validate endpoint. Returns the updated work-order status.
 */
export const validateJobResponseSchema = z.object({
  id: z.number(),
  work_order_status_id: z.number().nullable(),
  work_order_status_name: z.string().nullable(),
  validated: z.boolean(),
});

export type FormResponse = z.infer<typeof formResponseSchema>;
export type CapturedImage = z.infer<typeof capturedImageSchema>;
export type Note = z.infer<typeof noteSchema>;
export type DataCaptureResponse = z.infer<typeof dataCaptureResponseSchema>;
export type AddNoteBody = z.infer<typeof addNoteBodySchema>;
export type DataCaptureImageUploadUrlBody = z.infer<typeof dataCaptureImageUploadUrlBodySchema>;
export type DataCaptureImageUploadUrlResponse = z.infer<
  typeof dataCaptureImageUploadUrlResponseSchema
>;
export type DataCaptureImageCompleteBody = z.infer<typeof dataCaptureImageCompleteBodySchema>;
export type ValidateJobResponse = z.infer<typeof validateJobResponseSchema>;

/**
 * Data Capture API Contract
 *
 * Base path: /jobs/data-capture  (mounted in apps/backend/src/domains/works-lite/jobs/routes.ts)
 */
export const dataCaptureContract = c.router({
  getDataCapture: {
    method: 'GET',
    path: '/:jobId',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    responses: {
      200: dataCaptureResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get data capture information for a job',
    description: 'Returns form responses, captured images, and notes associated with a job.',
  },

  addDataCaptureNote: {
    method: 'POST',
    path: '/:jobId/notes',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    body: addNoteBodySchema,
    responses: {
      200: noteSchema,
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Add a quality-check note to a job',
    description: 'Inserts a bt_note row for the job and returns the created record.',
  },

  getDataCaptureImageUploadUrl: {
    method: 'POST',
    path: '/:jobId/images/upload-url',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    body: dataCaptureImageUploadUrlBodySchema,
    responses: {
      200: dataCaptureImageUploadUrlResponseSchema,
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Get a presigned S3 upload URL for a data-capture image',
  },

  completeDataCaptureImageUpload: {
    method: 'POST',
    path: '/:jobId/images/complete',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    body: dataCaptureImageCompleteBodySchema,
    responses: {
      200: capturedImageSchema,
      400: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Persist a data-capture image after S3 upload',
  },

  validateJob: {
    method: 'POST',
    path: '/:jobId/validate',
    pathParams: z.object({
      jobId: z.coerce.number(),
    }),
    body: z.object({}).optional(),
    responses: {
      200: validateJobResponseSchema,
      404: errorResponseSchema,
      500: errorResponseSchema,
    },
    summary: 'Mark a job as validated',
    description: 'Moves the job work-order status to "validated".',
  },
});
